import { useCallback } from 'react';
import { useReactFlow } from '@xyflow/react';

/**
 * Stable wrapper around `useReactFlow().screenToFlowPosition` that takes the
 * raw `(clientX, clientY)` pair commonly produced by DOM events and returns
 * the canvas-space `{ x, y }` point.
 *
 * Useful in pointer-event handlers (drawing overlays, eraser, paste-at-cursor,
 * helper-line viewport projection) so callers don't have to construct an
 * intermediate `{ x: e.clientX, y: e.clientY }` object on every event.
 *
 * The returned function reference is stable as long as React Flow keeps
 * `screenToFlowPosition` stable (which it does across renders of the same
 * canvas instance), so it is safe to include in `useCallback` / `useEffect`
 * dependency arrays without retriggering.
 */
export function useScreenToCanvasCoords(): (
  clientX: number,
  clientY: number,
) => { x: number; y: number } {
  const { screenToFlowPosition } = useReactFlow();
  return useCallback(
    (clientX: number, clientY: number) =>
      screenToFlowPosition({ x: clientX, y: clientY }),
    [screenToFlowPosition],
  );
}
